import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Lock, BarChart3, UserCheck, Mail, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';

/**
 * Informed Consent (Consentimiento Informado) — bare full-screen page.
 *
 * Conforme a la Ley 81 de 26 de marzo de 2019 sobre Protección de Datos
 * Personales de la República de Panamá. Three checkboxes are surfaced:
 *   - mandatory_1: general consent to process personal data
 *   - mandatory_2: explicit consent for sensitive personality / psychometric data
 *   - optional_future: optional consent to retain data for future opportunities
 *
 * Submission is gated on both mandatory checkboxes; the optional one is logged
 * separately on the server (see backend/routes/me.py::accept_consent).
 */
export default function EndUserConsent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useApp();
  const isES = (i18n.language || '').startsWith('es');

  const [m1, setM1] = useState(false);
  const [m2, setM2] = useState(false);
  const [opt, setOpt] = useState(false);
  const [saving, setSaving] = useState(false);

  // Skip the screen entirely if the server already has consent on file.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/me/consent');
        if (alive && data?.accepted) {
          if (setUser && user) setUser({ ...user, consent_accepted_at: data.accepted_at });
          navigate('/me', { replace: true });
        }
      } catch { /* not accepted yet — stay on this page */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = m1 && m2 && !saving;

  const persist = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post('/me/consent', {
        mandatory_1: m1,
        mandatory_2: m2,
        optional_future: opt,
      });
      if (setUser && user) setUser({ ...user, consent_accepted_at: data.accepted_at });
      toast.success(isES ? 'Consentimiento aceptado' : 'Consent accepted');
      // Iter 41.34 — first-time evaluated users see /me/profile before /me.
      // If profile_intro_seen is already true (e.g. consent re-accept), skip.
      const next = user?.profile_intro_seen ? '/me' : '/me/profile';
      navigate(next, { replace: true });
    } catch (e) {
      toast.error(isES ? 'No se pudo guardar' : 'Could not save', { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { icon: FileCheck2, key: 'purpose' },
    { icon: Lock,        key: 'confidentiality' },
    { icon: BarChart3,   key: 'use' },
    { icon: UserCheck,   key: 'rights' },
    { icon: Mail,        key: 'contact' },
  ];

  return (
    <div className="space-y-6" data-testid="consent-screen">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {t('pages.endUserConsent.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('pages.endUserConsent.subtitle')}
          </p>
        </div>
      </div>

      {/* Body sections */}
      <Card className="p-6 lg:p-8 shadow-card space-y-6" data-testid="consent-card">
        {sections.map((s) => (
          <section key={s.key} data-testid={`consent-section-${s.key}`} className="flex gap-4">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <s.icon className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <h2 className="font-semibold text-base">
                {t(`pages.endUserConsent.placeholder.${s.key}.title`)}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {t(`pages.endUserConsent.placeholder.${s.key}.body`)}
              </p>
            </div>
          </section>
        ))}

        {/* Checkbox gate — 2 mandatory + 1 optional */}
        <div className="pt-4 border-t border-border space-y-4">
          <div className="flex items-start gap-3" data-testid="consent-row-mandatory1">
            <Checkbox
              id="consent-m1"
              checked={m1}
              onCheckedChange={(v) => setM1(!!v)}
              className="mt-1"
              data-testid="consent-checkbox-mandatory1"
            />
            <label htmlFor="consent-m1" className="text-sm leading-relaxed cursor-pointer flex-1">
              <Badge variant="destructive" className="mr-2 align-middle text-xs" data-testid="consent-badge-required-1">
                {t('pages.endUserConsent.checkboxes.requiredBadge')}
              </Badge>
              {t('pages.endUserConsent.checkboxes.mandatory1')}
            </label>
          </div>

          <div className="flex items-start gap-3" data-testid="consent-row-mandatory2">
            <Checkbox
              id="consent-m2"
              checked={m2}
              onCheckedChange={(v) => setM2(!!v)}
              className="mt-1"
              data-testid="consent-checkbox-mandatory2"
            />
            <label htmlFor="consent-m2" className="text-sm leading-relaxed cursor-pointer flex-1">
              <Badge variant="destructive" className="mr-2 align-middle text-xs" data-testid="consent-badge-required-2">
                {t('pages.endUserConsent.checkboxes.requiredBadge')}
              </Badge>
              {t('pages.endUserConsent.checkboxes.mandatory2')}
            </label>
          </div>

          <div className="flex items-start gap-3" data-testid="consent-row-optional">
            <Checkbox
              id="consent-opt"
              checked={opt}
              onCheckedChange={(v) => setOpt(!!v)}
              className="mt-1"
              data-testid="consent-checkbox-optional"
            />
            <label htmlFor="consent-opt" className="text-sm leading-relaxed cursor-pointer flex-1">
              <Badge variant="secondary" className="mr-2 align-middle text-xs" data-testid="consent-badge-optional">
                {t('pages.endUserConsent.checkboxes.optionalBadge')}
              </Badge>
              {t('pages.endUserConsent.checkboxes.optional')}
            </label>
          </div>

          {!(m1 && m2) && (
            <p className="text-xs text-muted-foreground" data-testid="consent-required-hint">
              {t('pages.endUserConsent.checkboxes.requiredHint')}
            </p>
          )}
        </div>
      </Card>

      {/* Sticky action bar */}
      <div className="flex justify-end">
        <Button
          size="lg"
          className="bg-gradient-primary hover:opacity-90"
          disabled={!canSubmit}
          onClick={persist}
          data-testid="consent-agree-btn"
        >
          {saving
            ? t('pages.endUserConsent.saving', { defaultValue: isES ? 'Guardando…' : 'Saving…' })
            : t('pages.endUserConsent.agree', { defaultValue: isES ? 'Acepto y continuar' : 'Accept and continue' })}
        </Button>
      </div>
    </div>
  );
}
